import React, { useEffect, useRef, useState } from 'react';

// Lightweight Google reCAPTCHA v2 checkbox wrapper without extra deps.
// Renders an explicit v2 widget and reports verification via `onVerify`.

const SCRIPT_ID = 'recaptcha-v2-script';
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

const ReCaptchaV2 = ({
  siteKey = process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY || process.env.REACT_APP_RECAPTCHA_SITE_KEY || '',
  theme = 'light',
  size = 'normal',
  tabIndex = 0,
  onVerify,
  onExpire,
  onError,
  style,
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [ready, setReady] = useState(false);

  // Load the reCAPTCHA v2 script once
  useEffect(() => {
    const ensureScript = () => {
      if (window.grecaptcha && window.grecaptcha.render) {
        setReady(true);
        return;
      }

      if (!document.getElementById(SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onerror = () => onError && onError(new Error('Failed to load reCAPTCHA script'));
        script.onload = () => setReady(true);
        document.body.appendChild(script);
      } else {
        // Script exists; poll until grecaptcha is ready
        const i = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.render) {
            clearInterval(i);
            setReady(true);
          }
        }, 100);
        return () => clearInterval(i);
      }
    };

    ensureScript();
  }, [onError]);

  // Render the widget when ready
  useEffect(() => {
    if (!ready || !containerRef.current || widgetIdRef.current !== null) return;
    try {
      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        size,
        tabindex: tabIndex,
        callback: (token) => onVerify && onVerify(token),
        'expired-callback': () => onExpire && onExpire(),
        'error-callback': () => onError && onError(new Error('reCAPTCHA error')),
      });
    } catch (e) {
      onError && onError(e);
    }
  }, [ready, siteKey, theme, size, tabIndex, onVerify, onExpire, onError]);

  return (
    <div style={style}>
      <div ref={containerRef} />
    </div>
  );
};

export default ReCaptchaV2;

